import { useIntl } from 'react-intl';

import { ApiWorkflowDialogMessages } from '@verantos-dev/shared-components';

import { createEditUserType } from '../components/UsersListCreateEditDialog';

export const useUsersListCreateEditDialogStrings = (
  dialogType: createEditUserType,
  apiErrorCode?: number
) => {
  const intl = useIntl();

  // strings
  const dialogStrings = {
    title:
      dialogType === 'createUser'
        ? intl.formatMessage({
            id: 'users.createEditDialog.createTitle',
            defaultMessage: 'Create user',
          })
        : intl.formatMessage({
            id: 'users.createEditDialog.editTitle',
            defaultMessage: 'Edit user details',
          }),
    description: intl.formatMessage({
      id: 'users.createEditDialog.createDescription',
      defaultMessage: 'All fields are required unless they’re marked optional.',
    }),

    // section titles
    userDetailsSection: intl.formatMessage({
      id: 'users.createEditDialog.section.userDetails',
      defaultMessage: 'User details',
    }),
    organizationSection: intl.formatMessage({
      id: 'users.createEditDialog.section.organization',
      defaultMessage: 'Organization',
    }),
    rolesSection: intl.formatMessage({
      id: 'users.createEditDialog.section.userRoles',
      defaultMessage: 'User role(s)',
    }),

    // first name field
    firstNameLabel: intl.formatMessage({
      id: 'users.createEditDialog.firstNameLabel',
      defaultMessage: 'First name',
    }),
    firstNamePlaceholder: intl.formatMessage({
      id: 'users.createEditDialog.firstNamePlaceholder',
      defaultMessage: 'Enter first name',
    }),
    firstNameMaxLength: intl.formatMessage({
      id: 'users.createEditDialog.firstNameMaxLength',
      defaultMessage: 'First name max length is 60 characters',
    }),

    // last name field
    lastNameLabel: intl.formatMessage({
      id: 'users.createEditDialog.lastNameLabel',
      defaultMessage: 'Last name',
    }),
    lastNamePlaceholder: intl.formatMessage({
      id: 'users.createEditDialog.lastNamePlaceholder',
      defaultMessage: 'Enter last name',
    }),
    lastNameMaxLength: intl.formatMessage({
      id: 'users.createEditDialog.lastNameMaxLength',
      defaultMessage: 'Last name max length is 60 characters',
    }),

    // email field
    emailLabel: intl.formatMessage({
      id: 'users.createEditDialog.emailLabel',
      defaultMessage: 'Email',
    }),
    emailPlaceholder: intl.formatMessage({
      id: 'users.createEditDialog.emailPlaceholder',
      defaultMessage: 'Enter email',
    }),
    emailInvalidMessage: intl.formatMessage({
      id: 'users.createEditDialog.emailInvalidMessage',
      defaultMessage: 'Please enter a valid email address',
    }),

    // organization field
    organizationLabel: intl.formatMessage({
      id: 'users.createEditDialog.organizationLabel',
      defaultMessage: 'Organization',
    }),
    organizationPlaceholder: intl.formatMessage({
      id: 'users.createEditDialog.organizationPlaceholder',
      defaultMessage: 'Select organization',
    }),

    // roles field
    roleAnnotationSupervisor: intl.formatMessage({
      id: 'users.createEditDialog.role.AnnottonSupervisor',
      defaultMessage: 'Annotation supervisor',
    }),
    roleAnnotator: intl.formatMessage({
      id: 'users.createEditDialog.role.Annotator',
      defaultMessage: 'Annotator',
    }),
    roleDataAdmin: intl.formatMessage({
      id: 'users.createEditDialog.role.DataAdmin',
      defaultMessage: 'Data administrator',
    }),
    roleResearchPartner: intl.formatMessage({
      id: 'users.createEditDialog.role.ResearchPartner',
      defaultMessage: 'Research partner',
    }),
    roleResearcher: intl.formatMessage({
      id: 'users.createEditDialog.role.Researcher',
      defaultMessage: 'Researcher',
    }),
    roleStudyAdmin: intl.formatMessage({
      id: 'users.createEditDialog.role.StudyAdmin',
      defaultMessage: 'Study administrator',
    }),
    roleSysAdmin: intl.formatMessage({
      id: 'users.createEditDialog.role.SysAdmin',
      defaultMessage: 'System administrator',
    }),
    rolesEmpty: intl.formatMessage({
      id: 'users.createEditDialog.rolesEmpty',
      defaultMessage: 'Roles will become available after an organization has been selected.',
    }),
    rolesResearchPartnerNotSupported: intl.formatMessage({
      id: 'users.createEditDialog.role.rolesResearchPartnerNotSupported',
      defaultMessage: 'Research partner organization is not supported at this time',
    }),
  };

  // workflow dialog messages
  const apiWorkflowDialogMessages: ApiWorkflowDialogMessages = {
    entrySaveButtonText:
      dialogType === 'createUser'
        ? intl.formatMessage({
            id: 'users.createEditDialog.saveCreateBtnLabel',
            defaultMessage: 'Create user',
          })
        : intl.formatMessage({
            id: 'users.createEditDialog.saveEditBtnLabel',
            defaultMessage: 'Save changes',
          }),
    successText:
      dialogType === 'createUser'
        ? intl.formatMessage({
            id: 'users.createEditDialog.successCreateTextCreate',
            defaultMessage: 'User was created successfully.',
          })
        : intl.formatMessage({
            id: 'users.createEditDialog.successEditTextCreate',
            defaultMessage: 'User was updated successfully.',
          }),
    submittingErrorText:
      apiErrorCode === 409
        ? intl.formatMessage({
            id: 'users.createEditDialog.errorTextUserExists',
            defaultMessage: 'User with this email already exists.',
          })
        : intl.formatMessage({
            id: 'users.createEditDialog.errorTextEdit',
            defaultMessage:
              'User could not be updated due to a system error. You can retry creating the user by selecting the action below.',
          }),
    submittingSaveButtonText:
      dialogType === 'createUser'
        ? intl.formatMessage({
            id: 'users.createEditDialog.submittingSaveBtnText',
            defaultMessage: 'Create user',
          })
        : intl.formatMessage({
            id: 'users.createEditDialog.savingEditBtnLabel',
            defaultMessage: 'Create user',
          }),
    submittingErrorSaveButtonText: intl.formatMessage({
      id: 'users.createEditDialog.submittingErrorSaveBtnText',
      defaultMessage: 'Retry',
    }),
    submittingErrorCancelButtonText: intl.formatMessage({
      id: 'users.createEditDialog.submittingErrorCancelBtnText',
      defaultMessage: 'Close',
    }),
  };

  return { dialogStrings, apiWorkflowDialogMessages };
};
